import React from 'react';
import { Link } from 'react-router-dom';
import SEO from './SEO';

const Hero = () => {
  return (
    <>
      <SEO
        path="/"
        description="InjectAI Labs is an AI research company building production-ready RAG and AI solutions for enterprise platforms — available as WordPress plugin, Moodle plugin, and REST API."
      />
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-dark pt-20">
      {/* Background glows */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/10 rounded-full filter blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-secondary/8 rounded-full filter blur-3xl" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-primary/5 rounded-full filter blur-3xl" />
      </div>

      {/* Grid overlay */}
      <div className="absolute inset-0 opacity-[0.03]"
        style={{ backgroundImage: 'linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)', backgroundSize: '60px 60px' }} />

      <div className="relative z-10 max-w-6xl mx-auto px-6 text-center">
        {/* Research badge */}
        <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 border border-primary/20 rounded-full text-sm text-primary font-medium mb-8">
          <span className="w-2 h-2 bg-primary rounded-full animate-pulse" />
          AI Research Company
        </div>

        <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold mb-6 leading-[1.1] tracking-tight">
          Advancing AI for<br />
          <span className="bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
            Enterprise Platforms
          </span>
        </h1>

        <p className="text-xl md:text-2xl text-gray-400 mb-10 max-w-3xl mx-auto leading-relaxed">
          InjectAI Labs researches and ships production-ready AI — our RAG engine plugs into any platform, no rebuild needed.
        </p>

        <div className="flex flex-col sm:flex-row gap-4 justify-center mb-16">
          <Link to="/research" className="px-8 py-4 bg-primary hover:bg-blue-600 rounded-xl font-semibold transition-all transform hover:scale-105 text-base">
            Explore Our Research
          </Link>
          <Link to="/integrations" className="px-8 py-4 border border-gray-700 hover:border-primary hover:bg-primary/5 rounded-xl font-semibold transition-all text-base text-gray-300">
            View Integrations
          </Link>
        </div>

        {/* EmDash Plugin callout */}
        <div className="max-w-2xl mx-auto mb-16">
          <div className="bg-gray-900/60 border border-green-400/20 hover:border-green-400/40 transition-colors rounded-2xl p-6 text-left">
            <div className="flex items-center gap-3 mb-3">
              <span className="px-2.5 py-0.5 rounded-full text-xs font-medium border text-green-400 bg-green-400/10 border-green-400/20">
                New
              </span>
              <span className="text-sm font-semibold text-white">EmDash Plugin</span>
              <span className="text-xs text-gray-500">— AI chatbot for EmDash CMS</span>
            </div>
            <p className="text-sm text-gray-400 mb-4">
              Add a fully-featured RAG chatbot to any EmDash + Astro site. Upload your content, let your visitors ask questions.
            </p>
            <div className="flex items-center gap-3 bg-black/40 border border-gray-700 rounded-xl px-4 py-2.5 font-mono text-sm text-green-400">
              <svg className="w-4 h-4 text-gray-500 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
                <path d="M0 0h24v24H0V0zm19.2 4.8H4.8C3.81 4.8 3 5.61 3 6.6v10.8c0 .99.81 1.8 1.8 1.8h14.4c.99 0 1.8-.81 1.8-1.8V6.6c0-.99-.81-1.8-1.8-1.8zm-9.6 10.8H7.8V9.6h1.8v6zm4.8 0h-1.8V9.6h1.8v6zm4.8 0h-3.6V9.6h1.8v4.2h1.8v-4.2h1.8v6z"/>
              </svg>
              <span>npm install @injectailabs/emdash-injectai</span>
              <a
                href="https://www.npmjs.com/package/@injectailabs/emdash-injectai"
                target="_blank"
                rel="noopener noreferrer"
                className="ml-auto text-xs text-gray-500 hover:text-primary transition-colors whitespace-nowrap"
              >
                npm ↗
              </a>
            </div>
          </div>
        </div>

        {/* Stats row */}
        <div className="grid grid-cols-3 gap-8 max-w-2xl mx-auto border-t border-gray-800 pt-12">
          {[
            { value: 'RAG', label: 'Production Ready' },
            { value: '5GB+', label: 'File Support' },
            { value: 'Multi-LLM', label: 'Provider Support' },
          ].map((s, i) => (
            <div key={i} className="text-center">
              <div className="text-2xl md:text-3xl font-bold text-white mb-1">{s.value}</div>
              <div className="text-sm text-gray-500">{s.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
    </>
  );
};

export default Hero;
